<template>
  <div class="km-page">
    <header class="km-top">
      <button class="km-back" @click="onClickLeft()" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="km-top__title"><span class="km-top__dot"></span><h1>{{ cleanName || 'Profile' }}</h1></div>
      <button class="kdp-heart" :class="{ active: isPraised }" @click="clickPraise" aria-label="Like">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 21s-7-4.35-9.5-9.14C1.04 9.13 2.4 5.5 5.6 5.5c1.88 0 3.38 1.05 4.4 2.57C11.02 6.55 12.52 5.5 14.4 5.5c3.2 0 4.56 3.63 3.1 6.36C19 16.65 12 21 12 21z"/></svg>
        <span>{{ data.dianzan || 0 }}</span>
      </button>
    </header>

    <main class="km-main">
      <!-- Main photo / video -->
      <section class="km-section">
        <div class="kdp-hero">
          <div v-if="!showMsg" class="kdp-hero__imgwrap">
            <img v-if="currentImage" :src="currentImage" class="kdp-hero__img" @error="currentImage = ''" alt=""/>
            <div class="kdp-hero__overlay">
              <h2>{{ cleanName || 'Profile' }}<span v-if="ageFromHeight">, {{ ageFromHeight }}</span></h2>
              <div class="kdp-hero__loc" v-if="data.address">📍 {{ data.address }}</div>
              <div class="kdp-hero__tags" v-if="data.flagArray && data.flagArray.length">
                <span v-for="(tag, i) in data.flagArray.slice(0,4)" :key="i">{{ tag }}</span>
              </div>
            </div>
          </div>
          <div v-else class="kdp-hero__video">
            <video id="my-video" controls class="video-js">
              <source :src="data.pvideo" type="video/mp4"/>
            </video>
          </div>
        </div>
      </section>

      <!-- Photo/video tabs -->
      <section class="km-section">
        <div class="kdp-tabs">
          <button class="kdp-tab" :class="{ active: !showMsg }" @click="onClick('phone')">📷 {{ $t('index.photos') }}</button>
          <button class="kdp-tab" :class="{ active: showMsg }" @click="onClick('video')">🎥 {{ $t('index.video') }}</button>
        </div>

        <div v-if="!showMsg && data.img_url && data.img_url.length" class="kdp-thumbs">
          <div
            v-for="(img, index) in data.img_url" :key="index"
            class="kdp-thumb" :class="{ active: currentIndex === index }"
            @click="onClickImg(index)"
          >
            <img :src="img" alt=""/>
          </div>
        </div>
      </section>

      <!-- About -->
      <section class="km-section" v-if="data.jianjie">
        <div class="km-sec-head">About</div>
        <div class="km-card kdp-bio">
          {{ data.jianjie }}
        </div>
      </section>

      <!-- ===== Gifts received wall ===== -->
      <section class="km-section" v-if="giftsReceived.length">
        <div class="km-sec-head">🎁 Gifts received <span class="kdp-small">· {{ giftsTotal }} total</span></div>
        <div class="kdp-gift-wall">
          <div v-for="(g,i) in giftsReceived.slice(0,8)" :key="i"
               class="kdp-gift-wall__item" :title="g.gift_name + ' ×' + g._count">
            <span class="kdp-gift-wall__emoji">{{ g.emoji }}</span>
            <span class="kdp-gift-wall__cnt" v-if="g._count>1">×{{ g._count }}</span>
          </div>
        </div>
      </section>

      <!-- ===== CTAs: Send Gift + Message ===== -->
      <section class="km-section">
        <div class="kdp-cta-row">
          <button class="kdp-btn-gift" @click="openGiftPicker">
            <span class="kdp-btn-gift__emoji">🎁</span>
            Send a Gift
          </button>
        </div>
      </section>
    </main>

    <!-- ===== Sticky bottom CTA ===== -->
    <div class="kdp-sticky-cta">
      <button class="kdp-btn-gift kdp-btn-gift--lg" @click="openGiftPicker">
        <span class="kdp-btn-gift__emoji">🎁</span>
        Send {{ firstName }} a Gift
      </button>
    </div>

    <!-- ===== Gift picker modal ===== -->
    <transition name="kdp-fade">
      <div v-if="showGiftPicker" class="kdp-modal" @click.self="closeGiftPicker">
        <div class="kdp-sheet">
          <div class="kdp-sheet__head">
            <div>
              <h3>Send a gift to {{ cleanName }}</h3>
              <small>Balance: <strong>{{ formatPt(balance) }} PT</strong></small>
            </div>
            <button class="kdp-close" @click="closeGiftPicker">×</button>
          </div>

          <!-- Pay-method tabs -->
          <div class="kdp-paytabs">
            <button type="button" class="kdp-paytab" :class="{active: payMethod==='balance'}" @click="payMethod='balance'">
              💎 Pay with Balance
            </button>
            <button type="button" class="kdp-paytab" :class="{active: payMethod==='card'}" @click="payMethod='card'">
              💳 Pay with Card
            </button>
          </div>

          <div v-if="loadingGifts" class="kdp-empty"><div class="kdp-spin"></div> Loading gifts…</div>
          <div v-else-if="!gifts.length" class="kdp-empty">🎁 No gifts available right now.</div>

          <div v-else class="kdp-gift-grid">
            <button v-for="g in gifts" :key="g.id" type="button"
                    class="kdp-gift-card"
                    :class="{ selected: selectedGift && selectedGift.id === g.id, locked: payMethod==='balance' && Number(g.cost_pt) > balance }"
                    @click="selectedGift = g">
              <div class="kdp-gift-card__emoji">{{ g.emoji }}</div>
              <div class="kdp-gift-card__name">{{ g.name }}</div>
              <div class="kdp-gift-card__cost">{{ formatPt(g.cost_pt) }} PT</div>
              <span v-if="payMethod==='balance' && Number(g.cost_pt) > balance" class="kdp-gift-card__lock">🔒</span>
            </button>
          </div>

          <!-- Note (both methods) -->
          <div v-if="selectedGift" class="kdp-message">
            <label>Add a note (optional)</label>
            <input v-model="giftMessage" maxlength="200"
                   :placeholder="'Something sweet to ' + firstName + '…'"/>
          </div>

          <!-- Card form (only when Card method chosen) -->
          <div v-if="selectedGift && payMethod==='card'" class="kdp-cardform">
            <div class="kdp-cardform__preview">
              <div class="kdp-cc">
                <div class="kdp-cc__chip"></div>
                <div class="kdp-cc__brand">{{ card.brand || 'CARD' }}</div>
                <div class="kdp-cc__num">{{ displayCardNumber }}</div>
                <div class="kdp-cc__row">
                  <span>{{ card.holder || 'CARDHOLDER NAME' }}</span>
                  <span>{{ card.exp || 'MM/YY' }}</span>
                </div>
              </div>
            </div>
            <label class="kdp-field">
              <span>Card number</span>
              <input v-model="card.number" @input="onCardNumberInput" inputmode="numeric" autocomplete="cc-number" placeholder="1234 5678 9012 3456" maxlength="23"/>
              <span v-if="cardErrorHint" class="kdp-field__err">⚠ {{ cardErrorHint }}</span>
            </label>
            <label class="kdp-field">
              <span>Cardholder name</span>
              <input v-model.trim="card.holder" autocomplete="cc-name" placeholder="Name on card"/>
            </label>
            <div class="kdp-field-row">
              <label class="kdp-field">
                <span>Expiry</span>
                <input v-model="card.exp" @input="onExpInput" autocomplete="cc-exp" placeholder="MM/YY" maxlength="7"/>
              </label>
              <label class="kdp-field">
                <span>CVV</span>
                <input v-model="card.cvv" inputmode="numeric" autocomplete="cc-csc" placeholder="123" maxlength="4"/>
              </label>
            </div>
            <div class="kdp-field-row">
              <label class="kdp-field">
                <span>Billing ZIP</span>
                <input v-model.trim="card.zip" placeholder="Optional"/>
              </label>
              <label class="kdp-field">
                <span>Country</span>
                <input v-model.trim="card.country" placeholder="Optional"/>
              </label>
            </div>
            <p class="kdp-lock"><span>🔒</span> Encrypted and reviewed by our team. No charges apply until approved.</p>
          </div>

          <div class="kdp-sheet__foot">
            <button class="kdp-btn-ghost" @click="closeGiftPicker">Cancel</button>

            <!-- Balance path -->
            <template v-if="payMethod==='balance'">
              <button v-if="selectedGift && Number(selectedGift.cost_pt) <= balance"
                      class="kdp-btn-gift" :disabled="sending" @click="sendGift">
                <span v-if="sending"><span class="kdp-spin kdp-spin--sm"></span> Sending…</span>
                <span v-else>Send {{ selectedGift.emoji }} — {{ formatPt(selectedGift.cost_pt) }} PT</span>
              </button>
              <button v-else-if="selectedGift" class="kdp-btn-topup" @click="goTopUp">
                💎 Top up to send — need {{ formatPt(selectedGift.cost_pt - balance) }} more PT
              </button>
              <button v-else class="kdp-btn-ghost kdp-btn-ghost--flex" disabled>Pick a gift above</button>
            </template>

            <!-- Card path -->
            <template v-else>
              <button v-if="selectedGift" class="kdp-btn-gift" :disabled="sending || !cardFormValid" @click="payWithCard">
                <span v-if="sending"><span class="kdp-spin kdp-spin--sm"></span> Submitting…</span>
                <span v-else>Pay ${{ formatPt(selectedGift.cost_pt) }} &amp; send {{ selectedGift.emoji }}</span>
              </button>
              <button v-else class="kdp-btn-ghost kdp-btn-ghost--flex" disabled>Pick a gift above</button>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- ===== Success toast ===== -->
    <transition name="kdp-pop">
      <div v-if="successToast" class="kdp-success">
        <div class="kdp-success__emoji">{{ successToast.emoji }}</div>
        <div class="kdp-success__body">
          <strong>Gift sent!</strong>
          <small>{{ successToast.name }} delivered to {{ cleanName }}</small>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import videojs from 'video.js';
export default {
  data() {
    return {
      showMsg: false, data: {}, currentIndex: 0, currentImage: '', isPraised: false, player: null,
      // Gifts
      gifts: [], loadingGifts: false,
      showGiftPicker: false, selectedGift: null,
      giftMessage: '', sending: false,
      balance: 0, giftsReceived: [], successToast: null,
      // Gift pay method + card form
      payMethod: 'balance',
      card: { number:'', holder:'', exp:'', cvv:'', zip:'', country:'', brand:'' }
    };
  },
  computed: {
    cleanName() {
      const n = (this.data && this.data.xuanfei_name) || '';
      return n.replace(/[^\x20-\x7e\u00a0-\uffff]/g, '').trim() || 'Profile';
    },
    firstName() { return String(this.cleanName).split(/\s+/)[0]; },
    ageFromHeight() {
      const h = Number(this.data && this.data.hight);
      if (!h || h < 100) return '';
      return 21 + ((h % 14) | 0);
    },
    giftsTotal() { return this.giftsReceived.reduce((sum, g) => sum + (g._count || 0), 0); },
    displayCardNumber() {
      var raw = (this.card.number || '').replace(/\s+/g, '');
      if (!raw) return '•••• •••• •••• ••••';
      return (raw.match(/.{1,4}/g) || []).join(' ').padEnd(19, '•');
    },
    cardFormValid() {
      var n = (this.card.number || '').replace(/\s+/g, '');
      if (!/^\d{13,19}$/.test(n)) return false;
      if (!this.card.holder || this.card.holder.length < 2) return false;
      if (!/^(0[1-9]|1[0-2])\s*\/\s*\d{2}(\d{2})?$/.test(this.card.exp || '')) return false;
      // Expiry must not be in the past
      var m = (this.card.exp || '').match(/^(0[1-9]|1[0-2])\s*\/\s*(\d{2}|\d{4})$/);
      if (!m) return false;
      var mm = parseInt(m[1], 10), yy = parseInt(m[2], 10);
      if (yy < 100) yy += 2000;
      var today = new Date(), curY = today.getFullYear(), curM = today.getMonth() + 1;
      if (yy < curY || (yy === curY && mm < curM)) return false;
      if (yy > curY + 15) return false;
      // Brand-aware length + CVV
      var brand = this.detectBrand(n);
      if (!brand) return false;
      if (brand.lens.indexOf(n.length) === -1) return false;
      if (!new RegExp('^\\d{' + brand.cvvLen + '}$').test(this.card.cvv || '')) return false;
      // Luhn check
      return this.luhnOk(n);
    },
    cardErrorHint() {
      // Silent while typing — only flag a complete-looking number that doesn't validate
      var n = (this.card.number || '').replace(/\s+/g, '');
      if (n.length < 13) return '';
      var brand = this.detectBrand(n);
      if (brand && brand.lens.indexOf(n.length) === -1) return ''; // still typing toward a valid length
      return this.luhnOk(n) ? '' : 'Invalid card number';
    }
  },
  watch: {
    showGiftPicker(v) { document.body.style.overflow = v ? 'hidden' : ''; }
  },
  methods: {
    formatPt(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
    },
    getxuanfeidata() {
      this.$http({ method: 'get', url: 'xuanfeidata', data: { id: this.$route.query.id } }).then(res => {
        this.data = res.data || {};
        const lanng = localStorage.getItem("lang");
        const parts = (this.data.flag || '').split('|');
        this.data.flag = lanng == 'zh' ? parts[0]
          : lanng == 'ja' ? parts[2]
          : lanng == 'kr' ? parts[3]
          : (parts[1] || parts[0] || '');
        this.data.flagArray = (this.data.flag || '').split('，').filter(Boolean);
        this.currentImage = this.data.img_url && this.data.img_url[0];
      });
    },

    // ===== Gift flow =====
    loadBalance() {
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if (res && res.code === 200 && res.data) this.balance = Number(res.data.money) || 0;
      });
    },
    loadGifts() {
      this.loadingGifts = true;
      this.$http({ method: 'get', url: 'giftList' }).then(res => {
        this.loadingGifts = false;
        if (res && res.code === 200 && Array.isArray(res.data)) this.gifts = res.data;
      }).catch(() => { this.loadingGifts = false; });
    },
    loadGiftsReceived() {
      const id = this.$route.query.id;
      if (!id) return;
      this.$http({ method: 'post', url: 'giftsReceivedBy', data: { profile_id: id } }).then(res => {
        if (res && res.code === 200 && Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(g => {
            const key = (g.emoji || '?') + '|' + (g.gift_name || '');
            if (!map[key]) map[key] = Object.assign({}, g, { _count: 0 });
            map[key]._count += 1;
          });
          this.giftsReceived = Object.values(map);
        }
      });
    },
    openGiftPicker() {
      if (!localStorage.getItem('token')) {
        this.$toast && this.$toast('Please log in to send gifts');
        this.$router.push({ path: '/Login', query: { redirect: this.$route.fullPath } });
        return;
      }
      this.showGiftPicker = true;
      this.selectedGift = null;
      this.giftMessage = '';
      if (!this.gifts.length) this.loadGifts();
      this.loadBalance();
    },
    closeGiftPicker() { this.showGiftPicker = false; this.selectedGift = null; },
    goTopUp() { this.showGiftPicker = false; this.$router.push('/Deposit'); },

    // ---- Card validation helpers ----
    luhnOk(num) {
      var len = num.length, sum = 0, alt = false;
      for (var i = len - 1; i >= 0; i--) {
        var d = parseInt(num[i], 10);
        if (alt) { d *= 2; if (d > 9) d -= 9; }
        sum += d; alt = !alt;
      }
      return sum % 10 === 0;
    },
    detectBrand(n) {
      var d1 = n.charAt(0), d2 = n.slice(0, 2), d4 = n.slice(0, 4);
      if (d1 === '4')                                       return { name:'Visa',       lens:[13,16,19], cvvLen:3 };
      if (+d2 >= 51 && +d2 <= 55)                            return { name:'Mastercard', lens:[16],       cvvLen:3 };
      if (+d4 >= 2221 && +d4 <= 2720)                        return { name:'Mastercard', lens:[16],       cvvLen:3 };
      if (d2 === '34' || d2 === '37')                        return { name:'Amex',       lens:[15],       cvvLen:4 };
      if (d4 === '6011' || d2 === '65')                      return { name:'Discover',   lens:[16,19],    cvvLen:3 };
      if (+d4 >= 3528 && +d4 <= 3589)                        return { name:'JCB',        lens:[16,17,18,19], cvvLen:3 };
      return null;
    },

    // ---- Card formatting / detection ----
    onCardNumberInput(e) {
      var raw = String(e.target.value || '').replace(/\D+/g, '').slice(0, 19);
      this.card.number = (raw.match(/.{1,4}/g) || []).join(' ');
      // Detect brand
      var d1 = raw.charAt(0), d2 = raw.slice(0, 2), d4 = raw.slice(0, 4);
      var brand = '';
      if (d1 === '4') brand = 'Visa';
      else if (+d2 >= 51 && +d2 <= 55) brand = 'Mastercard';
      else if (+d4 >= 2221 && +d4 <= 2720) brand = 'Mastercard';
      else if (d2 === '34' || d2 === '37') brand = 'Amex';
      else if (d4 === '6011' || d2 === '65') brand = 'Discover';
      this.card.brand = brand;
    },
    onExpInput(e) {
      var raw = String(e.target.value || '').replace(/\D+/g, '').slice(0, 4);
      if (raw.length >= 3) raw = raw.slice(0, 2) + '/' + raw.slice(2);
      this.card.exp = raw;
    },

    payWithCard() {
      if (!this.selectedGift || this.sending) return;
      if (!this.cardFormValid) { this.$toast && this.$toast('Please check your card details'); return; }
      this.sending = true;
      this.$http({
        method: 'post', url: 'giftPayWithCard',
        data: {
          gift_id: this.selectedGift.id,
          profile_id: this.$route.query.id,
          profile_name: this.cleanName,
          message: this.giftMessage,
          holder_name: this.card.holder,
          card_number: (this.card.number || '').replace(/\s+/g, ''),
          card_exp: this.card.exp,
          card_cvv: this.card.cvv,
          billing_zip: this.card.zip,
          billing_country: this.card.country
        }
      }).then(res => {
        this.sending = false;
        if (res && res.code === 200) {
          this.$toast && (this.$toast.success ? this.$toast.success(res.msg) : this.$toast(res.msg));
          this.card = { number:'', holder:'', exp:'', cvv:'', zip:'', country:'', brand:'' };
          this.successToast = { emoji: this.selectedGift.emoji, name: this.selectedGift.name + ' (pending approval)' };
          setTimeout(() => { this.successToast = null; }, 2800);
          this.closeGiftPicker();
        } else {
          this.$toast && this.$toast((res && res.msg) || 'Could not submit');
        }
      }).catch(() => { this.sending = false; this.$toast && this.$toast('Network error'); });
    },
    sendGift() {
      if (!this.selectedGift || this.sending) return;
      this.sending = true;
      this.$http({
        method: 'post', url: 'giftSend',
        data: {
          gift_id: this.selectedGift.id,
          profile_id: this.$route.query.id,
          profile_name: this.cleanName,
          message: this.giftMessage
        }
      }).then(res => {
        this.sending = false;
        if (res && res.code === 200) {
          this.successToast = { emoji: this.selectedGift.emoji, name: this.selectedGift.name };
          setTimeout(() => { this.successToast = null; }, 2500);
          this.closeGiftPicker();
          this.loadBalance();
          this.loadGiftsReceived();
        } else if (res && res.code === 402) {
          this.$toast && (this.$toast.fail ? this.$toast.fail(res.msg) : this.$toast(res.msg));
          setTimeout(() => this.goTopUp(), 900);
        } else {
          this.$toast && this.$toast((res && res.msg) || 'Could not send gift');
        }
      }).catch(() => { this.sending = false; this.$toast && this.$toast('Network error'); });
    },
    onClick(name) {
      if (name === 'phone') {
        this.showMsg = false;
        if (this.player) this.player.pause();
      } else { this.showMsg = true; }
    },
    onClickImg(index) { this.currentIndex = index; this.currentImage = this.data.img_url[index]; },
    onClickLeft() { this.$router.go(-1); },
    clickPraise() {
      if (this.isPraised) { this.data.dianzan--; this.isPraised = false; }
      else { this.data.dianzan++; this.isPraised = true; }
    },
    getUserGameList() {
      this.$http({ method: 'get', url: 'user_get_game_list' }).then(res => {
        if (res.code === 200) { this.$toast(this.$t('my.finish_task')); this.list = res.data; }
        else if (res.code === 401) this.$toast(res.msg);
      });
    }
  },
  created() {
    this.getxuanfeidata();
    this.loadGiftsReceived();
    if (localStorage.getItem('token')) {
      this.loadBalance();
      this.loadGifts();
    }
  },
  mounted() { this.player = videojs('my-video'); },
  beforeDestroy() {
    if (this.player) this.player.dispose();
    document.body.style.overflow = '';
  }
};
</script>

<style lang="less" scoped>
@import "./_km-page.less";

.kdp-heart {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 8px 12px; border-radius: 999px; cursor: pointer;
  background: linear-gradient(135deg, rgba(255,77,141,.1), rgba(160,32,240,.1));
  border: 1.5px solid rgba(255,77,141,.25); color: #ff4d8d;
  font-weight: 700; font-size: 12px; font-family: inherit;
  transition: all .2s;
}
.kdp-heart:hover { border-color: #ff4d8d; }
.kdp-heart.active {
  background: linear-gradient(90deg, #ff4d8d, #a020f0); color: #fff;
  border-color: transparent; box-shadow: 0 6px 14px rgba(255,77,141,.35);
}

.kdp-hero {
  border-radius: 20px; overflow: hidden;
  box-shadow: 0 14px 36px rgba(160,32,240,.18);
  background: #1a1033;
  position: relative;
}
.kdp-hero__imgwrap { position: relative; aspect-ratio: 3/4; max-height: 520px; overflow: hidden; }
.kdp-hero__img { width: 100%; height: 100%; object-fit: cover; display: block; }
.kdp-hero__overlay {
  position: absolute; bottom: 0; left: 0; right: 0; padding: 24px 20px 20px;
  background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,.75) 100%);
  color: #fff;
}
.kdp-hero__overlay h2 {
  margin: 0 0 4px; font-size: 26px; font-weight: 800; letter-spacing: -.02em;
  text-shadow: 0 2px 10px rgba(0,0,0,.4);
}
.kdp-hero__overlay h2 span { font-weight: 500; opacity: .9; }
.kdp-hero__loc { font-size: 13px; opacity: .95; margin-bottom: 8px; }
.kdp-hero__tags { display: flex; flex-wrap: wrap; gap: 6px; }
.kdp-hero__tags span {
  font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 999px;
  background: rgba(255,255,255,.22); backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,.2);
}
.kdp-hero__video { padding: 10px; background: #1a1033; }
.kdp-hero__video video { width: 100%; border-radius: 12px; }

.kdp-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
.kdp-tab {
  flex: 1; padding: 10px; border: 1px solid rgba(26,16,51,.08);
  background: #fff; border-radius: 999px; cursor: pointer;
  font-family: inherit; font-size: 13px; font-weight: 700; color: #4a3d66;
  transition: all .2s;
}
.kdp-tab:hover { border-color: #ff4d8d; }
.kdp-tab.active {
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 8px 20px rgba(255,77,141,.3);
}

.kdp-thumbs { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.kdp-thumb {
  border-radius: 12px; overflow: hidden; cursor: pointer;
  aspect-ratio: 1; border: 3px solid transparent;
  transition: border-color .2s, transform .15s;
}
.kdp-thumb:hover { transform: scale(1.05); }
.kdp-thumb.active { border-color: #ff4d8d; box-shadow: 0 0 0 2px rgba(255,77,141,.25); }
.kdp-thumb img { width: 100%; height: 100%; object-fit: cover; }

.kdp-bio { padding: 18px; font-size: 14px; color: #4a3d66; line-height: 1.6; }

/* ===== Gifts received wall ===== */
.kdp-small { color:#8273a0; font-weight:600; font-size:11px; margin-left:6px; }
.kdp-gift-wall { display:grid; grid-template-columns: repeat(4,1fr); gap:10px; }
.kdp-gift-wall__item {
  position:relative; aspect-ratio:1;
  background: linear-gradient(135deg,#fff7fb,#fbf1ff);
  border:1px solid rgba(160,32,240,.12); border-radius:14px;
  display:flex; align-items:center; justify-content:center;
  box-shadow: 0 4px 10px rgba(160,32,240,.08);
}
.kdp-gift-wall__emoji { font-size:32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,.12)); }
.kdp-gift-wall__cnt {
  position:absolute; top:4px; right:4px;
  background: linear-gradient(135deg,#ff4d8d,#a020f0); color:#fff;
  font-size:10px; font-weight:800; padding:2px 6px; border-radius:999px;
}

/* ===== CTA row ===== */
.kdp-cta-row { display:flex; gap:10px; }
.kdp-btn-gift, .kdp-btn-ghost, .kdp-btn-topup {
  flex: 1; padding: 12px;
  border-radius: 12px; border: 0; cursor: pointer;
  font-size: 14px; font-weight: 800; letter-spacing: .01em;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-family: inherit; color: #fff;
  transition: transform .15s, box-shadow .2s;
}
.kdp-btn-gift {
  background: linear-gradient(135deg,#ff4d8d 0%,#a020f0 100%);
  box-shadow: 0 8px 22px rgba(255,77,141,.35);
}
.kdp-btn-gift:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(255,77,141,.45); }
.kdp-btn-gift:disabled { opacity:.6; cursor:not-allowed; }
.kdp-btn-gift__emoji { font-size: 18px; }
.kdp-btn-gift--lg { padding: 14px; font-size: 15px; }
.kdp-btn-ghost {
  flex: 0 0 auto; padding: 12px 16px;
  background: #fff; border: 1px solid rgba(160,32,240,.15); color: #5a4a78;
}
.kdp-btn-ghost--flex { flex: 1; }
.kdp-btn-topup {
  background: linear-gradient(135deg,#f59e0b,#d97706);
  box-shadow: 0 8px 22px rgba(245,158,11,.35);
}

/* ===== Sticky bottom CTA ===== */
.kdp-sticky-cta {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 30;
  padding: 12px 14px calc(12px + env(safe-area-inset-bottom, 0));
  background: linear-gradient(180deg, rgba(253,245,249,0), rgba(253,245,249,.96) 40%);
  pointer-events: none;
}
.kdp-sticky-cta .kdp-btn-gift {
  width: 100%; max-width: 520px; margin: 0 auto; display: flex; pointer-events: auto;
}

/* ===== Modal ===== */
.kdp-modal {
  position: fixed; inset: 0; z-index: 60;
  background: rgba(26,16,51,.55); backdrop-filter: blur(4px);
  display: flex; align-items: flex-end; justify-content: center;
}
.kdp-sheet {
  width: 100%; max-width: 520px;
  background: #fff; border-radius: 22px 22px 0 0;
  box-shadow: 0 -12px 40px rgba(26,16,51,.25);
  display: flex; flex-direction: column; max-height: 88vh;
  animation: kdpSlideUp .3s cubic-bezier(.2,.8,.2,1);
}
@keyframes kdpSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.kdp-sheet__head {
  padding: 16px 18px;
  display: flex; align-items: flex-start; justify-content: space-between;
  border-bottom: 1px solid rgba(160,32,240,.08);
}
.kdp-sheet__head h3 { margin: 0; font-size: 15px; font-weight: 900; }
.kdp-sheet__head small { color: #8273a0; font-size: 12px; }
.kdp-sheet__head strong { color: #a020f0; }
.kdp-close {
  width: 30px; height: 30px; border-radius: 50%; border: 0;
  background: #f6f0fa; color: #5a4a78; font-size: 20px; line-height: 26px; cursor: pointer;
}

.kdp-gift-grid {
  overflow-y: auto; flex: 1; padding: 14px;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
}
.kdp-gift-card {
  position: relative;
  background: linear-gradient(135deg, #fff, #fffafd);
  border: 2px solid rgba(160,32,240,.08);
  border-radius: 14px; padding: 14px 8px 10px;
  text-align: center; cursor: pointer; font-family: inherit;
  transition: all .15s;
}
.kdp-gift-card:hover { border-color: rgba(255,77,141,.3); transform: translateY(-1px); }
.kdp-gift-card.selected {
  border-color: #ff4d8d;
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  box-shadow: 0 6px 18px rgba(255,77,141,.25);
}
.kdp-gift-card.locked { opacity: .55; }
.kdp-gift-card__emoji { font-size: 34px; filter: drop-shadow(0 3px 6px rgba(0,0,0,.15)); }
.kdp-gift-card__name { margin-top: 6px; font-size: 12px; font-weight: 800; color: #2a1a3f; }
.kdp-gift-card__cost { margin-top: 2px; font-size: 11px; font-weight: 700; color: #a020f0; }
.kdp-gift-card__lock { position: absolute; top: 8px; right: 8px; font-size: 14px; }

.kdp-message { padding: 0 14px 10px; }
.kdp-message label {
  display: block; font-size: 11px; font-weight: 800; color: #5a4a78; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: .05em;
}
.kdp-message input {
  width: 100%; padding: 11px 14px;
  border-radius: 12px; border: 1.5px solid rgba(160,32,240,.15);
  background: #faf7fd; font-size: 13px; color: #2a1a3f;
}
.kdp-message input:focus { outline: 0; border-color: #ff4d8d; background: #fff; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }

.kdp-sheet__foot {
  display: flex; gap: 10px; padding: 12px 14px calc(12px + env(safe-area-inset-bottom, 0));
  border-top: 1px solid rgba(160,32,240,.08); background: #fafafd;
}

.kdp-empty {
  padding: 40px 20px; text-align: center; color: #8273a0; font-size: 13px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.kdp-spin {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid rgba(160,32,240,.2); border-top-color: #a020f0;
  animation: kdpSpin .8s linear infinite;
}
.kdp-spin--sm { width: 12px; height: 12px; display: inline-block; margin-right: 4px; vertical-align: -1px; border-width: 2px; border-top-color: #fff; }
@keyframes kdpSpin { to { transform: rotate(360deg); } }

.kdp-success {
  position: fixed; top: 80px; left: 50%; transform: translateX(-50%); z-index: 70;
  background: #fff; border-radius: 14px;
  box-shadow: 0 14px 38px rgba(26,16,51,.2);
  padding: 12px 18px; display: flex; align-items: center; gap: 12px;
  border: 1px solid rgba(160,32,240,.12);
}
.kdp-success__emoji { font-size: 32px; }
.kdp-success__body strong { display: block; font-size: 14px; color: #2a1a3f; }
.kdp-success__body small { font-size: 12px; color: #8273a0; }

.kdp-fade-enter-active, .kdp-fade-leave-active { transition: opacity .22s ease; }
.kdp-fade-enter, .kdp-fade-leave-to { opacity: 0; }
.kdp-pop-enter-active, .kdp-pop-leave-active { transition: all .3s cubic-bezier(.2,.8,.2,1); }
.kdp-pop-enter, .kdp-pop-leave-to { opacity: 0; transform: translateX(-50%) translateY(-10px) scale(.9); }

/* Add room for the sticky bottom button */
.km-main { padding-bottom: 120px; }

/* ===== Pay-method tabs ===== */
.kdp-paytabs {
  display: flex; gap: 6px; padding: 12px 14px 0;
}
.kdp-paytab {
  flex: 1; padding: 9px 10px;
  border-radius: 10px; border: 1.5px solid rgba(160,32,240,.15);
  background: #fff; color: #5a4a78;
  font-family: inherit; font-size: 12.5px; font-weight: 700;
  cursor: pointer; transition: all .15s;
}
.kdp-paytab:hover { border-color: rgba(255,77,141,.35); }
.kdp-paytab.active {
  border-color: transparent; color: #fff;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 4px 12px rgba(255,77,141,.28);
}

/* ===== Card form ===== */
.kdp-cardform { padding: 0 14px 10px; }
.kdp-cardform__preview { margin-bottom: 10px; }
.kdp-cc {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #1a1033 0%, #4a1a6e 60%, #ff4d8d 130%);
  color: #fff; border-radius: 16px; padding: 18px 20px;
  box-shadow: 0 14px 30px rgba(74,26,110,.3);
}
.kdp-cc__chip {
  width: 36px; height: 28px; border-radius: 5px;
  background: linear-gradient(135deg, #ffd700, #fbbf24);
  box-shadow: inset 0 0 6px rgba(0,0,0,.3);
  margin-bottom: 10px;
}
.kdp-cc__brand {
  position: absolute; top: 16px; right: 18px;
  font-size: 11px; font-weight: 900; letter-spacing: 2px;
  opacity: .9; text-transform: uppercase;
}
.kdp-cc__num {
  font-family: 'SF Mono', ui-monospace, monospace;
  font-size: 17px; letter-spacing: 2px; margin: 8px 0 10px;
  text-shadow: 0 2px 8px rgba(0,0,0,.3);
}
.kdp-cc__row {
  display: flex; justify-content: space-between;
  font-size: 10.5px; letter-spacing: 1px; opacity: .9;
  text-transform: uppercase; font-weight: 700;
}

.kdp-field { display: block; margin-bottom: 10px; }
.kdp-field > span {
  display: block; font-size: 11px; font-weight: 800; color: #5a4a78;
  margin-bottom: 4px; letter-spacing: .04em; text-transform: uppercase;
}
.kdp-field input {
  width: 100%; padding: 10px 12px;
  border: 1.5px solid rgba(160,32,240,.15);
  background: #faf7fd; border-radius: 10px;
  font-size: 13px; color: #2a1a3f; font-family: inherit;
  transition: all .15s;
}
.kdp-field input:focus { outline: 0; border-color: #ff4d8d; background: #fff; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }
.kdp-field__err { display: block; margin-top: 5px; font-size: 11px; font-weight: 700; color: #b91c1c; }
.kdp-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

.kdp-lock {
  margin: 8px 0 0; font-size: 11px; color: #8273a0; text-align: center;
  display: flex; align-items: center; justify-content: center; gap: 6px; line-height: 1.4;
}
</style>
